import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

type Tenant = {
  id: string;
  name: string;
  slug: string;
  created_at: string;
  plan: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
  owner_email: string | null;
  member_count: number;
  jobs_last_30d: number;
  last_activity_at: string | null;
};

const PAGE_SIZE = 25;

const TenantList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['platform-tenants', search, status, page],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_tenants' as never, {
        _search: search || null,
        _status: status === 'all' ? null : status,
        _limit: PAGE_SIZE,
        _offset: page * PAGE_SIZE,
      } as never);
      if (error) throw error;
      return (data || []) as Tenant[];
    },
  });

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h2 className="font-display text-3xl text-foreground">Organisations</h2>
        <p className="mt-1 text-muted-foreground">Search, inspect, and support customer businesses.</p>
      </div>

      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="mb-4 grid gap-3 md:grid-cols-[1fr_220px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} placeholder="Search name or slug" className="pl-9" />
            </div>
            <Select value={status} onValueChange={(value) => { setStatus(value); setPage(0); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="past_due">Past due</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Unable to load organisations: {(error as Error).message}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Jobs 30d</TableHead>
                    <TableHead>Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((tenant) => (
                    <TableRow key={tenant.id} className="cursor-pointer" onClick={() => navigate(`/platform/tenants/${tenant.id}`)}>
                        <TableCell>
                          <div className="font-medium text-foreground">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground">{tenant.slug}</div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{tenant.subscription_status || 'unknown'}</Badge></TableCell>
                        <TableCell>{tenant.owner_email || '—'}</TableCell>
                        <TableCell>{tenant.member_count}</TableCell>
                        <TableCell>{tenant.jobs_last_30d}</TableCell>
                        <TableCell>{tenant.last_activity_at ? new Date(tenant.last_activity_at).toLocaleDateString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {data.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No tenants found</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}>
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            <Button variant="outline" size="sm" disabled={data.length < PAGE_SIZE} onClick={() => setPage((value) => value + 1)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantList;
